import { forwardRef } from 'react';
import { cn } from '../tokens/cn';
import type { VariantProps } from '../tokens/variants';
import { badgeVariants } from './badge.variants';

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Optional icon rendered to the left of the children. */
  leftIcon?: React.ReactNode;
  /** Renders a small filled dot before the content (status indicator). The
   *  dot inherits the current text color so it tints with the tone. */
  dot?: boolean;
}

/**
 * Compact label primitive. Use for tags, statuses, counts, lead types — any
 * place that previously used a hand-rolled `<span class="bg-… text-…">`.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    { tone, size, leftIcon, dot, className, children, ...rest },
    ref,
  ) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ tone, size }), className)}
      {...rest}
    >
      {dot && (
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-tk-full bg-current shrink-0"
        />
      )}
      {leftIcon}
      {children}
    </span>
  ),
);

Badge.displayName = 'Badge';
